import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge, SearchableSelect } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, useDetailReadOnly,
  FieldText, FieldSelect, FieldToggle, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';
import {
  DERIVATION_API, VENDOR_API, CUSTOMER_API, REPRESENTATIVE_API, WAREHOUSE_API,
  CRITERIA_ENDPOINTS, FAMILY_TONE, LOOKUP_PAGE,
  unwrap, makeLookupFetcher, resolveLabels,
} from './documentDerivationShared';

// BELGE TÜRETME TANIMI — EDİTÖR (menü 9561).
//
// Ekran İKİ PANELDİR: solda KAYNAK (hangi belge tetikler), sağda HEDEF (ne türetilir).
// Aradaki "TÜRETİR" oku yönü gösterir.
//
// ══ ALAN GÖRÜNÜRLÜĞÜ NEREDEN GELİR ═══════════════════════════════════════════════════════════
// Hangi alanın hangi ailede görüneceği BU DOSYADA KODLANMAZ: backend'in kayıt defteri her tip
// için `needsCustomer` / `needsWarehouse` / `isTransfer` / `requiresBank` bayraklarını döner ve
// form onları okur. Aynı kuralın ikinci bir kopyası olsaydı, kayıt defterine yeni bir belge tipi
// eklendiğinde ekran sessizce yanlış alanları gösterirdi.
//
// ══ HEDEF LİSTESİNİ DE BACKEND DARALTIR ══════════════════════════════════════════════════════
// Kaynak seçilince `/DerivableTargets` çağrılır. Kısıt ("benzer belgeden benzer belge") ORADA
// uygulanır; kaydetmede AYNI kısıt bir kez daha doğrulanır — UI filtresine güvenilmez.

const EMPTY_FORM = {
  description: '',
  isActive: true,

  sourceDocumentTypeId: '',
  sourceVendorId: '',
  sourceCustomerCriteriaType: '',
  sourceCustomerCriteriaValues: [],
  sourceCustomerId: '',
  sourceWarehouseId: '',
  sourceEntryTypeCode: '',
  sourceCancelOnDerivation: false,

  targetDocumentTypeId: '',
  targetVendorId: '',
  targetCustomerId: '',
  targetRepresentativeId: '',
  targetWarehouseId: '',
  targetFromWarehouseId: '',
  targetToWarehouseId: '',
  targetBankName: '',
  targetBankAccountNo: '',
  targetDescription: '',

  targetStockControl: false,
  targetRiskControl: false,
  targetCopySourceWarehouse: false,
  targetCopySourceSalesRep: false,
  targetSourceAddressAsShipTo: false,
  targetCopySerialDetails: false,
  targetEditable: false,
};

/** Hedef bayrakları ve hangi ailelerde anlamlı oldukları (doküman §4.B tablosu). */
const TARGET_FLAGS = [
  { k: 'targetStockControl', label: 'Stok Kontrol', families: ['ticari', 'stok'], hint: 'Yetersiz stokta türetme yapılmaz.' },
  { k: 'targetRiskControl', label: 'Risk Kontrol', families: ['ticari', 'finans'] },
  { k: 'targetCopySourceWarehouse', label: 'Kaynak Belgenin Deposu Birebir Aktarılsın', families: ['ticari', 'stok'] },
  { k: 'targetCopySourceSalesRep', label: 'Kaynak Belgenin Satış Temsilcisi Birebir Aktarılsın', families: ['ticari', 'finans'] },
  { k: 'targetSourceAddressAsShipTo', label: 'Kaynak Belge Adresi Sevk Adresine Atansın', families: ['ticari'] },
  { k: 'targetCopySerialDetails', label: 'Seri Detayları Aktarılsın', families: ['ticari', 'stok'] },
  {
    k: 'targetEditable',
    label: 'Belge Düzenlenebilir',
    families: ['ticari', 'stok', 'finans'],
    hint: 'Açıkken türetilen belge TASLAK doğar: stok/cari etkisi oluşmaz, kullanıcı düzeltip aktifleştirir.',
  },
];

/**
 * Aileye göre gruplanmış (optgroup'lu) belge tipi seçici.
 *
 * `FieldSelect` optgroup desteklemez ve kaynak listesi prototipte AİLEYE GÖRE gruplanmıştır —
 * 30 tipi düz bir listede taramak, kullanıcıyı yanlış aileden seçim yapmaya iter. DetailShell'in
 * salt-okunur bağlamı burada elle okunur (FieldCustom kilitlemez, yalnız görsel işaretler).
 */
function GroupedTypeSelect({ value, groups, onChange, invalid, req, label }) {
  const readOnly = useDetailReadOnly();
  return (
    <FieldCustom label={label} req={req} invalid={invalid} span>
      <div className="dps-ff-input">
        <select value={value} disabled={readOnly} onChange={(e) => onChange(e.target.value)}>
          <option value="">Seçiniz…</option>
          {groups.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </optgroup>
          ))}
        </select>
        <span className="dps-ff-chev"><Icon name="chevD" size={16} /></span>
      </div>
    </FieldCustom>
  );
}

export function DocumentDerivationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(
    isNew ? 'Yeni Türetme Tanımı' : 'Türetme Tanımı',
    'Kaynak belgenin başlık koşulu ve türetilecek hedef belgenin künyesi.',
  );

  const [form, setForm] = useState(EMPTY_FORM);
  const [code, setCode] = useState('');
  const [audit, setAudit] = useState(null);
  const [catalog, setCatalog] = useState({ types: [], families: [], criteriaTypes: [] });
  const [targetInfo, setTargetInfo] = useState({ targets: [], ruleHint: '', sourceFamily: '' });
  const [criteriaLabels, setCriteriaLabels] = useState({});
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState('definition');

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 4000); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  // ── Kayıt defteri ────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetch(`${DERIVATION_API}/DocumentTypes`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => { const d = unwrap(b); if (d) setCatalog(d); })
      .catch(() => {});
  }, []);

  const typeById = useMemo(
    () => Object.fromEntries((catalog.types || []).map((t) => [t.id, t])),
    [catalog],
  );
  const sourceType = typeById[form.sourceDocumentTypeId];
  const targetType = typeById[form.targetDocumentTypeId];
  const family = sourceType?.family || '';
  const isStockSource = family === 'stok';

  /** Kaynak combobox'ı — aileye göre gruplu (prototipteki optgroup düzeni). */
  const sourceGroups = useMemo(() => (catalog.families || []).map((f) => ({
    label: f.label,
    options: (catalog.types || []).filter((t) => t.family === f.key).map((t) => ({ value: t.id, label: t.name })),
  })).filter((g) => g.options.length > 0), [catalog]);

  // ── Hedef listesi: KISITI BACKEND UYGULAR ────────────────────────────────────────────────
  useEffect(() => {
    if (!form.sourceDocumentTypeId) { setTargetInfo({ targets: [], ruleHint: '', sourceFamily: '' }); return; }
    fetch(`${DERIVATION_API}/DerivableTargets?sourceTypeId=${encodeURIComponent(form.sourceDocumentTypeId)}`,
      { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        const d = unwrap(b);
        if (!d) return;
        setTargetInfo(d);
        // Mevcut hedef daralan listede yoksa temizle — geçersiz bir çift ekranda kalmamalı.
        setForm((c) => (c.targetDocumentTypeId && !d.targets.some((t) => t.id === c.targetDocumentTypeId)
          ? { ...c, targetDocumentTypeId: '' } : c));
      })
      .catch(() => {});
  }, [form.sourceDocumentTypeId]);

  // ── Kayıt yükleme ────────────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${DERIVATION_API}/GetById?id=${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Tanım alınamadı (${res.status})`);
      const r = unwrap(await res.json());
      setCode(r.code || '');
      setForm({
        ...EMPTY_FORM,
        ...Object.fromEntries(Object.keys(EMPTY_FORM).map((k) => [k, r[k] ?? EMPTY_FORM[k]])),
        sourceCustomerCriteriaValues: r.sourceCustomerCriteriaValues || [],
      });
      setAudit({
        createdBy: r.createdBy, createdDate: r.createdDate,
        updatedBy: r.updatedBy, updatedDate: r.updatedDate,
      });
    } catch (err) {
      showToast('error', err.message || 'Tanım alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // Türetme geçmişi — "bu tanım gerçekten çalışıyor mu?" sorusunu yanıtlar. BAŞARISIZLAR DA gelir.
  useEffect(() => {
    if (isNew) return;
    fetch(`${DERIVATION_API}/Logs?id=${id}&take=50`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => setLogs(unwrap(b) || []))
      .catch(() => {});
  }, [id, isNew]);

  // Seçili kriter değerlerinin etiketleri (chip gösterimi).
  useEffect(() => {
    const endpoint = CRITERIA_ENDPOINTS[form.sourceCustomerCriteriaType];
    if (!endpoint || form.sourceCustomerCriteriaValues.length === 0) return;
    resolveLabels(endpoint, form.sourceCustomerCriteriaValues).then(setCriteriaLabels).catch(() => {});
  }, [form.sourceCustomerCriteriaType, form.sourceCustomerCriteriaValues]);

  // ── Lookup okuyucuları ───────────────────────────────────────────────────────────────────
  const fetchVendors = useMemo(() => makeLookupFetcher(VENDOR_API), []);
  const fetchCustomers = useMemo(() => makeLookupFetcher(CUSTOMER_API, 'Title'), []);
  const fetchReps = useMemo(() => makeLookupFetcher(REPRESENTATIVE_API), []);
  const fetchWarehouses = useMemo(() => makeLookupFetcher(WAREHOUSE_API), []);
  const fetchCriteria = useMemo(() => {
    const endpoint = CRITERIA_ENDPOINTS[form.sourceCustomerCriteriaType];
    return endpoint ? makeLookupFetcher(endpoint, form.sourceCustomerCriteriaType === 'CUSTOMER' ? 'Title' : 'Name') : null;
  }, [form.sourceCustomerCriteriaType]);

  const addCriteriaValue = (value) => {
    if (!value || form.sourceCustomerCriteriaValues.includes(value)) return;
    set('sourceCustomerCriteriaValues', [...form.sourceCustomerCriteriaValues, value]);
  };
  const removeCriteriaValue = (value) =>
    set('sourceCustomerCriteriaValues', form.sourceCustomerCriteriaValues.filter((v) => v !== value));

  // ── Kaydetme ─────────────────────────────────────────────────────────────────────────────
  const buildBody = () => ({
    id: isNew ? null : id,
    ...form,
    description: form.description.trim(),
    // Boş string'ler Guid?'e bağlanamaz → null gönderilir.
    ...Object.fromEntries(Object.entries(form)
      .filter(([k, v]) => k.endsWith('Id') && v === '')
      .map(([k]) => [k, null])),
  });

  const persist = async () => {
    setTried(true);
    if (!form.description.trim()) { showToast('error', 'Açıklama zorunludur.'); return; }
    if (!form.sourceDocumentTypeId) { showToast('error', 'Kaynak belge tipi seçilmelidir.'); return; }
    if (!form.targetDocumentTypeId) { showToast('error', 'Hedef belge tipi seçilmelidir.'); return; }
    if (!form.sourceVendorId) { showToast('error', 'Kaynak distribütör zorunludur.'); return; }
    if (!form.targetVendorId) { showToast('error', 'Hedef distribütör zorunludur.'); return; }

    setSaving(true);
    try {
      const res = await fetch(`${DERIVATION_API}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(buildBody()),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        // Aile/enstrüman kısıtı ihlali backend'den ayrı bir kodla döner — kullanıcıya sebebiyle gösterilir.
        const errCode = body?.data?.code ?? body?.Data?.code;
        throw new Error((errCode === 'INVALID_DERIVATION_PAIR' ? 'Geçersiz türetme çifti — ' : '')
          + (body?.message || body?.Message || `İşlem başarısız (${res.status})`));
      }
      showToast('success', isNew ? 'Tanım oluşturuldu.' : 'Tanım güncellendi.');
      setTimeout(() => navigate('/document-derivations'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${DERIVATION_API}/Delete`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || `Silme başarısız (${res.status})`);
      showToast('success', 'Tanım silindi.');
      setTimeout(() => navigate('/document-derivations'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────────────────
  const statusBadge = !isNew && (
    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
      <StatusBadge tone={form.isActive ? 'ok' : 'neutral'}>{form.isActive ? 'Aktif' : 'Pasif'}</StatusBadge>
      {family && <StatusBadge tone={FAMILY_TONE[family] || 'neutral'}>
        {(catalog.families || []).find((f) => f.key === family)?.label || family}
      </StatusBadge>}
    </span>
  );

  const actions = (
    <>
      {!isNew && canDelete && (
        <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}>
          <Icon name="x" size={16} /> Sil
        </button>
      )}
      <button className="dps-btn ghost" onClick={() => navigate('/document-derivations')} disabled={saving}>İptal</button>
      {canSave && (
        <button className="dps-btn primary" onClick={persist} disabled={saving}>
          <Icon name="check" size={16} /> {saving ? 'Kaydediliyor…' : 'Kaydet ve Kapat'}
        </button>
      )}
    </>
  );

  const tabs = isNew ? undefined : [
    { key: 'definition', label: 'Tanım' },
    { key: 'history', label: `Türetme Geçmişi${logs.length ? ` (${logs.length})` : ''}` },
  ];

  const visibleFlags = TARGET_FLAGS.filter((f) => targetType && f.families.includes(targetType.family));

  return (
    <DetailShell
      crumb={[
        { label: 'Belge Türetme Tanımları', to: '/document-derivations' },
        { label: isNew ? 'Yeni Tanım' : `Tanım #${code || ''}`, current: true },
      ]}
      status={statusBadge}
      actions={actions}
      tabs={tabs}
      activeTab={activeTab}
      onTab={setActiveTab}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'DocumentDerivation', entityId: id }}
      loading={loading}
    >
      {activeTab === 'history' ? (
        <div className="dps-form">
          <Section
            icon="list"
            title="Türetme Geçmişi"
            subtitle="Bu tanımın hangi belgeden hangi belgeyi türettiği. Başarısız denemeler de listelenir."
          >
            {logs.length === 0 ? (
              <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
                Bu tanım henüz hiç belge türetmedi.
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
                      <th style={{ padding: '8px 10px' }}>Tarih</th>
                      <th style={{ padding: '8px 10px' }}>Kaynak</th>
                      <th style={{ padding: '8px 10px' }}>Hedef</th>
                      <th style={{ padding: '8px 10px' }}>Sonuç</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((l) => (
                      <tr key={l.id} style={{ borderTop: '1px solid var(--border-color, rgba(15,23,42,.08))' }}>
                        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                          {new Date(l.createdDate).toLocaleString('tr-TR')}
                        </td>
                        <td style={{ padding: '8px 10px' }}>{l.sourceDocumentTypeName || l.sourceDocumentTypeId}</td>
                        <td style={{ padding: '8px 10px' }}>{l.derivedDocumentTypeName || l.derivedDocumentTypeId}</td>
                        <td style={{ padding: '8px 10px' }}>
                          <StatusBadge tone={l.status === 'DERIVED' ? 'ok' : 'bad'}>
                            {l.status === 'DERIVED' ? 'Türetildi' : 'Başarısız'}
                          </StatusBadge>
                          {l.message && (
                            <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{l.message}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </div>
      ) : (
        <div className="dps-form">
          {/* Ortak şerit — Kod OTOMATİK, kullanıcı giremez. */}
          <Section icon="briefcase" title="Tanım Bilgileri" subtitle="Kod otomatik verilir; açıklama zorunludur">
            <Grid cols={3}>
              <FieldText
                form={{ code: isNew ? '(Otomatik)' : code }} set={() => {}} k="code"
                label="Kod" readOnly hint="Kayıtta sistem tarafından verilir."
              />
              <FieldSelect
                form={form} set={set} k="isActive" label="Durum" req
                options={[{ value: true, label: 'Aktif' }, { value: false, label: 'Pasif' }]}
                onChange={(v) => set('isActive', v === 'true' || v === true)}
              />
              <FieldText form={form} set={set} tried={tried} k="description" label="Açıklama" req
                placeholder="Ör. Nakit Tahsilattan Karşı Nakit Ödeme" />
            </Grid>
          </Section>

          {/* ── KAYNAK ── */}
          <Section
            icon="list"
            title="Kaynak Belge Bilgileri"
            subtitle="Bu koşula uyan belge türetmeyi tetikler — koşul yalnız başlık düzeyindedir"
          >
            <Grid cols={2}>
              <GroupedTypeSelect
                label="Belge Tipi" req
                value={form.sourceDocumentTypeId}
                groups={sourceGroups}
                invalid={tried && !form.sourceDocumentTypeId}
                onChange={(v) => set('sourceDocumentTypeId', v)}
              />

              <FieldCustom label="Distribütör" req invalid={tried && !form.sourceVendorId} span>
                <SearchableSelect
                  value={form.sourceVendorId}
                  onChange={(v) => set('sourceVendorId', v || '')}
                  fetchOptions={fetchVendors}
                  placeholder="Distribütör ara…"
                  pageSize={LOOKUP_PAGE}
                />
              </FieldCustom>

              {/* Stok ailesinde müşteri kriterinin YERİNE depo kriteri gelir. */}
              {isStockSource ? (
                <FieldCustom label="Depo Kriteri (boş = tüm depolar)" span>
                  <SearchableSelect
                    value={form.sourceWarehouseId}
                    onChange={(v) => set('sourceWarehouseId', v || '')}
                    fetchOptions={fetchWarehouses}
                    placeholder="Depo ara… (boş = tüm depolar)"
                    emptyOption="(Tüm depolar)"
                    pageSize={LOOKUP_PAGE}
                  />
                </FieldCustom>
              ) : (
                <>
                  <FieldSelect
                    form={form} set={set} k="sourceCustomerCriteriaType" label="Müşteri Kriter Tipi"
                    options={[{ value: '', label: '(Tüm müşteriler)' },
                      ...(catalog.criteriaTypes || []).map((c) => ({ value: c.key, label: c.label }))]}
                    onChange={(v) => setForm((c) => ({ ...c, sourceCustomerCriteriaType: v, sourceCustomerCriteriaValues: [] }))}
                    hint="Boş bırakılırsa distribütörün tüm müşterileri kapsanır."
                  />
                  <FieldCustom label="Müşteri Kriter">
                    {fetchCriteria ? (
                      <SearchableSelect
                        value=""
                        onChange={(v) => addCriteriaValue(v)}
                        fetchOptions={fetchCriteria}
                        placeholder="Ara ve ekle…"
                        pageSize={LOOKUP_PAGE}
                      />
                    ) : (
                      <div className="dps-ff-input">
                        <input value="" placeholder="Önce kriter tipi seçiniz" readOnly disabled />
                      </div>
                    )}
                  </FieldCustom>

                  {form.sourceCustomerCriteriaValues.length > 0 && (
                    <FieldCustom label="Seçili Kriterler" span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {form.sourceCustomerCriteriaValues.map((v) => (
                          <span key={v} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                            borderRadius: 999, background: 'var(--input-bg, #f3f7ff)', fontSize: 12,
                          }}>
                            {criteriaLabels[v] || v}
                            <button
                              type="button"
                              onClick={() => removeCriteriaValue(v)}
                              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#dc2626' }}
                              aria-label="Kaldır"
                            >×</button>
                          </span>
                        ))}
                      </div>
                    </FieldCustom>
                  )}

                  <FieldCustom label="Müşteri (tek müşteriye daraltmak için)" span>
                    <SearchableSelect
                      value={form.sourceCustomerId}
                      onChange={(v) => set('sourceCustomerId', v || '')}
                      fetchOptions={fetchCustomers}
                      placeholder="Müşteri ara… (opsiyonel)"
                      emptyOption="(Daraltma yok)"
                      pageSize={LOOKUP_PAGE}
                    />
                  </FieldCustom>
                </>
              )}

              <FieldText
                form={form} set={set} k="sourceEntryTypeCode" label="Giriş Tip" span
                placeholder="(opsiyonel)"
                hint="Bilgi amaçlı saklanır — üründe karşılığı olan bir belge alanı olmadığı için eşleştirmede KULLANILMAZ."
              />
            </Grid>

            <div style={{ marginTop: 16, borderTop: '1px solid var(--border-color, rgba(15,23,42,.08))', paddingTop: 12 }}>
              <FieldToggle
                form={form} set={set} k="sourceCancelOnDerivation"
                label="Belge Türetildiğinde Kaynak Belge İptal Edilsin?"
                hint="Kaynak belge Silindi durumuna geçer ve stok/cari etkisi geri sarılır."
                span
              />
            </div>
          </Section>

          {/* ── HEDEF ── */}
          <Section
            icon="check"
            title="Hedef Belge Bilgileri"
            subtitle="Otomatik türetilecek belgenin künyesi"
            note={targetInfo.ruleHint || 'Önce kaynak belge tipini seçin — hedef seçenekleri ona göre daralır.'}
          >
            <Grid cols={2}>
              <FieldSelect
                form={form} set={set} k="targetDocumentTypeId" label="Belge Tipi" req span
                options={[{ value: '', label: 'Seçiniz…' },
                  ...(targetInfo.targets || []).map((t) => ({ value: t.id, label: t.name }))]}
              />

              <FieldCustom label="Distribütör" req invalid={tried && !form.targetVendorId} span>
                <SearchableSelect
                  value={form.targetVendorId}
                  onChange={(v) => set('targetVendorId', v || '')}
                  fetchOptions={fetchVendors}
                  placeholder="Distribütör ara…"
                  pageSize={LOOKUP_PAGE}
                />
              </FieldCustom>

              {targetType?.needsCustomer && (
                <>
                  <FieldCustom label="Müşteri (boş = kaynaktan alınır)" span>
                    <SearchableSelect
                      value={form.targetCustomerId}
                      onChange={(v) => set('targetCustomerId', v || '')}
                      fetchOptions={fetchCustomers}
                      placeholder="Müşteri ara…"
                      emptyOption="(Kaynaktan)"
                      pageSize={LOOKUP_PAGE}
                    />
                  </FieldCustom>
                  <FieldCustom label="ST (Satış Temsilcisi)" span>
                    <SearchableSelect
                      value={form.targetRepresentativeId}
                      onChange={(v) => set('targetRepresentativeId', v || '')}
                      fetchOptions={fetchReps}
                      placeholder="Temsilci ara…"
                      emptyOption="(Kaynaktan)"
                      pageSize={LOOKUP_PAGE}
                    />
                  </FieldCustom>
                </>
              )}

              {targetType?.needsWarehouse && !targetType?.isTransfer && (
                <FieldCustom label="Depo" span>
                  <SearchableSelect
                    value={form.targetWarehouseId}
                    onChange={(v) => set('targetWarehouseId', v || '')}
                    fetchOptions={fetchWarehouses}
                    placeholder="Depo ara…"
                    emptyOption="(Kaynaktan)"
                    pageSize={LOOKUP_PAGE}
                  />
                </FieldCustom>
              )}

              {targetType?.isTransfer && (
                <>
                  <FieldCustom label="Çıkış Deposu" req invalid={tried && !form.targetFromWarehouseId}>
                    <SearchableSelect
                      value={form.targetFromWarehouseId}
                      onChange={(v) => set('targetFromWarehouseId', v || '')}
                      fetchOptions={fetchWarehouses}
                      placeholder="Çıkış deposu…"
                      pageSize={LOOKUP_PAGE}
                    />
                  </FieldCustom>
                  <FieldCustom label="Giriş Deposu" req invalid={tried && !form.targetToWarehouseId}>
                    <SearchableSelect
                      value={form.targetToWarehouseId}
                      onChange={(v) => set('targetToWarehouseId', v || '')}
                      fetchOptions={fetchWarehouses}
                      placeholder="Giriş deposu…"
                      pageSize={LOOKUP_PAGE}
                    />
                  </FieldCustom>
                </>
              )}

              {targetType?.requiresBank && (
                <>
                  <FieldText
                    form={form} set={set} tried={tried} k="targetBankName" label="Banka" req
                    placeholder="Banka adı"
                    hint="Serbest metindir — üründe banka master tablosu yoktur; değer enstrüman künyesine yazılır."
                  />
                  <FieldText form={form} set={set} k="targetBankAccountNo" label="Hesap No / IBAN" placeholder="(opsiyonel)" />
                </>
              )}

              <FieldText
                form={form} set={set} k="targetDescription" label="Açıklama" span
                placeholder="Boş bırakılırsa “Otomatik türetilen belge” yazılır"
              />
            </Grid>

            {visibleFlags.length > 0 && (
              <div style={{ marginTop: 16, borderTop: '1px solid var(--border-color, rgba(15,23,42,.08))', paddingTop: 12 }}>
                <div style={{
                  fontSize: 11, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase',
                  color: 'var(--text-secondary)', marginBottom: 8,
                }}>
                  Hedef Davranışı &amp; Kontroller
                </div>
                <Grid cols={2}>
                  {/* span → her anahtar tam genişlik; iki sütuna sıkışan uzun etiketler okunmuyordu. */}
                  {visibleFlags.map((f) => (
                    <FieldToggle key={f.k} form={form} set={set} k={f.k} label={f.label} hint={f.hint} span />
                  ))}
                </Grid>
              </div>
            )}
          </Section>
        </div>
      )}

      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={460}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>
                {saving ? 'Siliniyor…' : 'Evet, Sil'}
              </button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.description}”</strong> tanımını silmek istediğinize emin misiniz?
          </p>
          <p style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>
            Tanım bundan sonra hiçbir belge türetmez. Daha önce türetilmiş belgeler ve iz kayıtları KORUNUR.
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default DocumentDerivationDetailPage;
